/* PDP の初期お気に入り同期と初回解除を管理する */
(() => {
  if (window.AuremePdpWishlistInitialSync) return;

  const wishlistSdk = window.AuremeWishlistSdk;
  if (!wishlistSdk) {
    console.error('[aureme-pdp-wishlist-initial-sync] Wishlist Hero SDK helper is not loaded.');
    return;
  }

  const buttonState = window.AuremePdpWishlistButtonState;
  if (!buttonState) {
    console.error(
      '[aureme-pdp-wishlist-initial-sync] PDP wishlist button state helper is not loaded.',
    );
    return;
  }

  const createReadyRetryScheduler = window.AuremeReadyRetry?.createScheduler;
  if (typeof createReadyRetryScheduler !== 'function') {
    console.error('[aureme-pdp-wishlist-initial-sync] ready retry helper is not loaded.');
    return;
  }

  const PDP_INITIAL_SYNC_RETRY_INTERVAL_MS = 100;
  const PDP_INITIAL_SYNC_MAX_RETRY_COUNT = 100;

  const {
    createWishlistMutationContext,
    fetchWishlistItems,
    normalizeId,
    waitForWishlistSdk,
    wishlistItemsIncludeVariant,
  } = wishlistSdk;

  function getVariantIdFromCurrentUrl() {
    try {
      const variantId = new URL(window.location.href).searchParams.get('variant');
      return normalizeId(variantId);
    } catch {
      return '';
    }
  }

  function getVariantIdFromSelectedVariantJson() {
    const selectedVariantJson = document.querySelector('[data-selected-variant]')?.textContent;
    if (!selectedVariantJson) return '';

    try {
      return normalizeId(JSON.parse(selectedVariantJson)?.id);
    } catch {
      return '';
    }
  }

  function getVariantIdFromProductFormInput() {
    const productInfo = document.querySelector('product-info');
    const variantInput = productInfo?.querySelector(
      'product-form input.product-variant-id, product-form input[name="id"], form[action*="/cart/add"] input.product-variant-id, form[action*="/cart/add"] input[name="id"]',
    );

    return normalizeId(variantInput?.value);
  }

  function getCurrentVariantId() {
    return (
      getVariantIdFromCurrentUrl() ||
      getVariantIdFromSelectedVariantJson() ||
      getVariantIdFromProductFormInput()
    );
  }

  function createPdpWishlistInitialSyncController({
    onRemoveError = () => {},
    onRemoveSuccess = () => {},
    suppressExternalNotifications = () => {},
  } = {}) {
    let initialStateSyncStarted = false;
    let pdpWishlistMutationContext = null;
    const initialSyncRetryScheduler = createReadyRetryScheduler({
      intervalMs: PDP_INITIAL_SYNC_RETRY_INTERVAL_MS,
      maxRetryCount: PDP_INITIAL_SYNC_MAX_RETRY_COUNT,
    });
    const initialSyncedVariantIdByContainer = new WeakMap();
    const initialSyncedRemovingContainers = new WeakSet();

    function markInitialSyncedButtonAdded(container, productVariantId) {
      buttonState.applyAddedState(container);
      initialSyncedVariantIdByContainer.set(container, productVariantId);
    }

    function clearInitialSyncedButton(container) {
      initialSyncedVariantIdByContainer.delete(container);
    }

    async function syncInitialWishlistState(container) {
      const initialProductVariantId = getCurrentVariantId();
      if (!initialProductVariantId) return;

      try {
        const sdk = await waitForWishlistSdk();
        const wishlistItems = await fetchWishlistItems(sdk, {
          preferServer: true,
          serverFallbackWarning: '',
        });
        /*
         * SDK 取得中に variant が切り替わった場合は、初期 variant の結果を現在のボタンへ反映しない。
         */
        if (getCurrentVariantId() !== initialProductVariantId) return;
        if (!wishlistItemsIncludeVariant(wishlistItems, initialProductVariantId)) return;

        markInitialSyncedButtonAdded(container, initialProductVariantId);
      } catch {
        /* SDK 側で初期一覧を取得できない場合は、Wishlist Hero の標準描画に任せる */
      }
    }

    function start(retryCount = 0) {
      if (initialStateSyncStarted) return;

      initialSyncRetryScheduler.schedule({
        tryReady: () => {
          const container = buttonState.getContainer();
          if (!container || !buttonState.getButton(container)) return false;

          initialStateSyncStarted = true;
          syncInitialWishlistState(container);
          return true;
        },
        onRetry: start,
        retryCount,
      });
    }

    function getMutationContext(sdk) {
      if (!pdpWishlistMutationContext) {
        pdpWishlistMutationContext = createWishlistMutationContext(sdk, {
          serverFallbackWarning: '',
        });
      }

      return pdpWishlistMutationContext;
    }

    function setButtonDisabled(container, isDisabled) {
      const button = buttonState.getButton(container);
      if (!button) return;

      if ('disabled' in button) {
        button.disabled = isDisabled;
        return;
      }

      if (isDisabled) {
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      button.removeAttribute('aria-disabled');
    }

    function getInitialSyncedRemoveVariantId(container) {
      const initialSyncedVariantId = initialSyncedVariantIdByContainer.get(container);
      if (!initialSyncedVariantId) return '';
      if (!buttonState.isAdded(container)) return '';

      const currentVariantId = getCurrentVariantId();
      if (currentVariantId !== initialSyncedVariantId) return '';

      return currentVariantId;
    }

    async function removeInitialSyncedWishlistItem(container, productVariantId) {
      initialSyncedRemovingContainers.add(container);
      setButtonDisabled(container, true);

      try {
        const sdk = await waitForWishlistSdk();
        const mutationContext = getMutationContext(sdk);
        const wishlistHash = await mutationContext.getRequiredHash();
        const mutationResult = await mutationContext.mutateItem({
          isCurrentlyAdded: true,
          productInfo: null,
          productVariantId,
          wishlistHash,
        });

        clearInitialSyncedButton(container);
        const isCurrentVariantRemoved = getCurrentVariantId() === productVariantId;
        if (isCurrentVariantRemoved) {
          buttonState.applyRemovedState(container);
          onRemoveSuccess(mutationResult.isAdded);
        }
      } catch {
        onRemoveError();
      } finally {
        initialSyncedRemovingContainers.delete(container);
        setButtonDisabled(container, false);
      }
    }

    function handleRemoveClick(event, container) {
      if (initialSyncedRemovingContainers.has(container)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return true;
      }

      const productVariantId = getInitialSyncedRemoveVariantId(container);
      if (!productVariantId) return false;

      /*
        初期同期で見た目だけ補正した直後は Wishlist Hero 本体のクリック判定が
        未登録側に残ることがあるため、初回の解除だけテーマ側で実行する。
      */
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressExternalNotifications();
      removeInitialSyncedWishlistItem(container, productVariantId);
      return true;
    }

    return {
      handleRemoveClick,
      start,
    };
  }

  window.AuremePdpWishlistInitialSync = {
    createController: createPdpWishlistInitialSyncController,
  };
})();
